import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '@/ui/atoms/Button';
import { useConsent } from '@/store/consent';

// ConsentBanner — P5 GDPR Art.7 explicit consent surface.
// Privacy fix #1 (App.tsx mount) + #4 (gdprArt7ConsentText verbatim).
// Mounted once at App root; hides itself when state is accepted/declined/withdrawn.
export function ConsentBanner() {
  const { t } = useTranslation();
  const visible = useConsent((s) => s.bannerVisible);
  const accept = useConsent((s) => s.accept);
  const decline = useConsent((s) => s.decline);
  const [busy, setBusy] = useState<'accept' | 'decline' | null>(null);
  const [err, setErr] = useState<string | null>(null);

  if (!visible) return null;

  const onAccept = async () => {
    setBusy('accept');
    setErr(null);
    try {
      await accept();
    } catch {
      setErr(t('consent.errorAccept'));
    } finally {
      setBusy(null);
    }
  };

  const onDecline = async () => {
    setBusy('decline');
    setErr(null);
    try {
      await decline();
    } catch {
      setErr(t('consent.errorAccept'));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div
      className="aha-consent-banner-shell border-t border-hairline bg-paper px-6 py-5 shadow-lg"
      role="region"
      aria-label={t('consent.bannerHeading')}
      data-testid="consent-banner"
    >
      <div className="max-w-page mx-auto flex flex-col md:flex-row md:items-center gap-4">
        <div className="flex-1 text-sm text-ink-2 leading-relaxed">
          <p className="font-mono text-[11px] tracking-meta uppercase text-vermilion mb-1">
            {t('consent.bannerHeading')}
          </p>
          <p>{t('personalization.gdprArt7ConsentText')}</p>
          {err && <p className="mt-2 text-vermilion text-xs">{err}</p>}
        </div>
        <div className="flex gap-2 shrink-0">
          <Button
            variant="ghost"
            onClick={onDecline}
            disabled={busy !== null}
            data-testid="consent-decline"
          >
            {t('consent.decline')}
          </Button>
          <Button
            variant="vermilion"
            onClick={onAccept}
            loading={busy === 'accept'}
            disabled={busy !== null}
            data-testid="consent-accept"
          >
            {t('consent.accept')}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default ConsentBanner;
